import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Square, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface VoiceInputOverlayProps {
  open: boolean;
  onStop: () => void;
  onCancel: () => void;
  duration: number;
  audioLevel: number;
  interimTranscript: string;
  isProcessing: boolean;
  children: React.ReactNode; // trigger element
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function WaveformBars({ audioLevel }: { audioLevel: number }) {
  return (
    <div className="flex items-center justify-center gap-1 h-8">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="voice-bar"
          style={{
            height: `${Math.max(4, audioLevel * 32 * (0.5 + Math.random() * 0.5))}px`,
            transition: "height 0.1s ease",
          }}
        />
      ))}
    </div>
  );
}

function RecordingContent({
  onStop,
  onCancel,
  duration,
  audioLevel,
  interimTranscript,
  isProcessing,
}: Omit<VoiceInputOverlayProps, "open" | "children">) {
  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
          <span className="text-sm font-medium">
            {isProcessing ? "Processing..." : "Listening..."}
          </span>
        </div>
        <span className="text-sm text-muted-foreground font-mono">
          {formatDuration(duration)}
        </span>
      </div>

      {/* Waveform */}
      {!isProcessing && <WaveformBars audioLevel={audioLevel} />}

      {/* Processing spinner */}
      {isProcessing && (
        <div className="flex items-center justify-center h-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        </div>
      )}

      {/* Interim transcript */}
      {interimTranscript && (
        <div className="max-h-20 overflow-y-auto">
          <p className="text-sm text-muted-foreground italic leading-relaxed">
            {interimTranscript}
          </p>
        </div>
      )}

      {/* Actions */}
      {!isProcessing && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="destructive"
            className="flex-1"
            onClick={onStop}
          >
            <Square className="h-3 w-3 mr-1 fill-current" />
            Done
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function VoiceInputOverlay({
  open,
  onStop,
  onCancel,
  duration,
  audioLevel,
  interimTranscript,
  isProcessing,
  children,
}: VoiceInputOverlayProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    // On mobile, render the content inline below the trigger
    return (
      <>
        {children}
        {open && (
          <div className="mt-2 rounded-lg border bg-popover p-3 shadow-md animate-in fade-in-0 slide-in-from-top-2">
            <RecordingContent
              onStop={onStop}
              onCancel={onCancel}
              duration={duration}
              audioLevel={audioLevel}
              interimTranscript={interimTranscript}
              isProcessing={isProcessing}
            />
          </div>
        )}
      </>
    );
  }

  // Desktop: Popover
  return (
    <Popover open={open}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className={cn("w-72")}
        side="bottom"
        align="end"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <RecordingContent
          onStop={onStop}
          onCancel={onCancel}
          duration={duration}
          audioLevel={audioLevel}
          interimTranscript={interimTranscript}
          isProcessing={isProcessing}
        />
      </PopoverContent>
    </Popover>
  );
}
